import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { SiedStatusCell } from '../funerals/SiedStatusCell';
import { Sp3RowMenu } from './Sp3RowMenu';
import { RecordFilter, siedListCounts, siedListShown } from './siedList';
import {
  Box,
  Typography,
  Button,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Alert,
  Stepper,
  Step,
  StepLabel,
  IconButton,
  Link as MuiLink,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  InputAdornment,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import FiberNewIcon from '@mui/icons-material/FiberNew';
import ErrorIcon from '@mui/icons-material/Error';
import DownloadIcon from '@mui/icons-material/Download';
import AssignmentIcon from '@mui/icons-material/Assignment';
import SearchIcon from '@mui/icons-material/Search';
import { useNotify } from 'react-admin';
import { apiFetch } from '../lib/apiFetch';

interface SiedRecord {
  id: number;
  form_number: string;
  last_name: string;
  first_name: string;
  nam: string;
  date_of_death: string;
  date_of_birth: string;
  sp3_storage_path: string | null;
  detected_at: string;
  downloaded_at: string | null;
  notified_at: string | null;
  /** SIED process step (T-100 phase 1), when the status sync has run. */
  sied_status?: string | null;
  sied_status_raw?: string | null;
  sied_status_checked_at?: string | null;
  /** SIED status when the held PDF was downloaded (T-109); absent until migrated. */
  sp3_downloaded_status?: string | null;
  /** Removed as not ours (T-148): hidden unless the Removed filter is on. */
  dismissed_at?: string | null;
  dismissed_by?: string | null;
  dismissed_reason?: string | null;
  /** Case whose sp3_number is this form (GET sied/overview). */
  linked_case?: { id: number; name: string } | null;
}

interface MatchReason {
  code: string;
  label: string;
  strong: boolean;
}

interface CaseSuggestion {
  id: number;
  name: string;
  maidenName: string | null;
  dateOfDeath: string | null;
  score: number;
  reasons: MatchReason[];
}

interface SiedProgress {
  step: string;
  detail?: string;
  error?: string;
  completedSteps: string[];
}

interface SiedStatus {
  lastCheckedAt: string | null;
  isRunning: boolean;
  recordCount: number;
  progress: SiedProgress;
  lastError: string | null;
}

interface Funeral {
  id: number;
  deceased_first_name: string;
  deceased_last_name: string;
  created_at: string;
}

const STEPS = [
  { key: 'login', label: 'Login to SIED' },
  { key: 'fetch', label: 'Fetch records' },
  { key: 'compare', label: 'Compare' },
  { key: 'process', label: 'Process new records' },
  { key: 'status', label: 'Update case statuses' },
];

function timeAgo(dateStr: string): string {
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
}

function isNew(detectedAt: string): boolean {
  const diff = Date.now() - new Date(detectedAt).getTime();
  return diff < 24 * 60 * 60 * 1000;
}

function formatDate(dateStr: string | null): string {
  if (!dateStr) return '-';
  try {
    return new Date(dateStr).toLocaleString('en-CA', {
      timeZone: 'America/Toronto',
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return dateStr;
  }
}

function getActiveStep(progress: SiedProgress): number {
  if (progress.step === 'idle' || progress.step === 'starting') return 0;
  if (progress.step === 'done') return STEPS.length;
  if (progress.step === 'error') {
    const lastCompleted = progress.completedSteps[progress.completedSteps.length - 1];
    const idx = STEPS.findIndex((s) => s.key === lastCompleted);
    return idx + 1;
  }
  return STEPS.findIndex((s) => s.key === progress.step);
}

function isStepError(progress: SiedProgress, stepKey: string): boolean {
  if (progress.step !== 'error') return false;
  const activeIdx = getActiveStep(progress);
  const stepIdx = STEPS.findIndex((s) => s.key === stepKey);
  return stepIdx === activeIdx;
}

// Download is secondary: a small icon, or a muted hint until the PDF is in.
const Sp3Download = ({ record }: { record: SiedRecord }) => {
  const notify = useNotify();
  if (!record.sp3_storage_path) {
    return (
      <Typography variant="caption" color="text.disabled" title="SP-3 PDF not downloaded yet">
        PDF pending
      </Typography>
    );
  }
  const handleDownload = async () => {
    try {
      const res = await apiFetch(`sied/download/${encodeURIComponent(record.form_number)}`);
      window.open(res.url, '_blank');
    } catch {
      notify('Failed to download SP3', { type: 'error' });
    }
  };
  return (
    <IconButton size="small" title="Download SP-3 PDF" aria-label="Download SP-3 PDF" onClick={() => void handleDownload()}>
      <DownloadIcon fontSize="small" />
    </IconButton>
  );
};

// "#857 · Ana Chen" + Change, or amber "Not linked" + Assign to case.
const LinkedCaseCell = ({
  record,
  onAssign,
}: {
  record: SiedRecord;
  onAssign: (record: SiedRecord) => void;
}) => {
  if (record.linked_case) {
    return (
      <Box display="flex" alignItems="center" gap={1} minWidth={0}>
        <MuiLink
          component={RouterLink}
          to={`/funerals/${record.linked_case.id}/show`}
          underline="hover"
          sx={{ fontWeight: 500, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}
        >
          #{record.linked_case.id} · {record.linked_case.name}
        </MuiLink>
        <Button size="small" sx={{ minWidth: 0, color: 'text.secondary', textTransform: 'none' }} onClick={() => onAssign(record)}>
          Change
        </Button>
      </Box>
    );
  }
  return (
    <Box display="flex" alignItems="center" gap={1}>
      <Chip label="Not linked" size="small" color="warning" variant="outlined" sx={{ fontWeight: 600 }} />
      <Button size="small" variant="contained" disableElevation sx={{ textTransform: 'none', whiteSpace: 'nowrap' }} onClick={() => onAssign(record)}>
        Assign to case
      </Button>
    </Box>
  );
};

// Removed rows (T-148): who/when in place of the case (why on hover), and an undo.
const RemovedCell = ({ record, onRestored }: { record: SiedRecord; onRestored: () => void }) => {
  const notify = useNotify();
  const [busy, setBusy] = useState(false);
  const handleRestore = async () => {
    setBusy(true);
    try {
      await apiFetch(`sied/restore/${encodeURIComponent(record.form_number)}`, { method: 'POST' });
      notify(`SP-3 ${record.form_number} restored`, { type: 'success' });
      onRestored();
    } catch {
      notify('Failed to restore SP-3', { type: 'error' });
    } finally {
      setBusy(false);
    }
  };
  return (
    <Box display="flex" alignItems="center" gap={1} minWidth={0}>
      <Typography variant="body2" color="text.secondary" noWrap title={record.dismissed_reason ?? undefined}>
        Removed{record.dismissed_by ? ` by ${record.dismissed_by}` : ''}
        {record.dismissed_at ? ` · ${timeAgo(record.dismissed_at)}` : ''}
      </Typography>
      <Button size="small" sx={{ textTransform: 'none' }} disabled={busy} onClick={() => void handleRestore()}>
        Restore
      </Button>
    </Box>
  );
};

const REASON_SX = (strong: boolean) => ({
  fontSize: 11.5,
  px: 0.75,
  borderRadius: 999,
  color: strong ? 'success.dark' : 'warning.dark',
  bgcolor: strong ? 'rgba(22,163,74,0.10)' : 'rgba(245,158,11,0.14)',
});

const SEARCH_DEBOUNCE_MS = 300;

// Assign SP3 to funeral modal
const AssignModal = ({
  open,
  record,
  onClose,
  onAssigned,
}: {
  open: boolean;
  record: SiedRecord | null;
  onClose: () => void;
  onAssigned: () => void;
}) => {
  const [funerals, setFunerals] = useState<Funeral[]>([]);
  const [loadingFunerals, setLoadingFunerals] = useState(false);
  const [selectedFuneralId, setSelectedFuneralId] = useState<number | null>(null);
  const [assigning, setAssigning] = useState(false);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [suggestions, setSuggestions] = useState<CaseSuggestion[]>([]);
  // Monotonically increasing id so a slow earlier request can't clobber a
  // faster later one.
  const requestId = useRef(0);
  const notify = useNotify();

  // Every open starts clean: no selection, no leftover search.
  useEffect(() => {
    if (!open) return;
    setSelectedFuneralId(null);
    setSearch('');
    setDebouncedSearch('');
    setSuggestions([]);
  }, [open]);

  // Top 2 matching cases (name incl. maiden name, date of death ±1 day).
  useEffect(() => {
    if (!open || !record) return;
    let live = true;
    void apiFetch(`sied/assign/suggestions/${encodeURIComponent(record.form_number)}`)
      .then((res) => {
        if (!live) return;
        const list: CaseSuggestion[] = res.suggestions || [];
        setSuggestions(list);
        if (list.length) setSelectedFuneralId((cur) => cur ?? list[0].id);
      })
      .catch(() => live && setSuggestions([]));
    return () => {
      live = false;
    };
  }, [open, record]);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(search.trim()), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [search]);

  // An empty search asks the backend for its default page (the newest
  // funerals); a non-empty one asks it to search the whole funerals table.
  // Filtering `funerals` here instead would only ever find what is already
  // loaded — exactly the case the search box exists to fix.
  useEffect(() => {
    if (!open) return;
    const currentRequest = ++requestId.current;
    setLoadingFunerals(true);
    const path = debouncedSearch
      ? `sied/funerals/recent?search=${encodeURIComponent(debouncedSearch)}`
      : 'sied/funerals/recent';
    void apiFetch(path)
      .then((res) => {
        if (currentRequest !== requestId.current) return;
        setFunerals(res.funerals || []);
      })
      .catch(() => {
        if (currentRequest !== requestId.current) return;
        setFunerals([]);
        notify('Failed to load funerals', { type: 'error' });
      })
      .finally(() => {
        if (currentRequest !== requestId.current) return;
        setLoadingFunerals(false);
      });
  }, [open, debouncedSearch, notify]);

  const handleAssign = async () => {
    if (!record || !selectedFuneralId) return;
    setAssigning(true);
    try {
      const res = await apiFetch('sied/assign-case', {
        method: 'POST',
        body: JSON.stringify({
          formNumber: record.form_number,
          funeralId: selectedFuneralId,
        }),
      });
      notify(
        res?.documentError
          ? `SP-3 linked to case #${selectedFuneralId}, but the PDF wasn't filed: ${res.documentError}`
          : `SP-3 linked to case #${selectedFuneralId}`,
        { type: res?.documentError ? 'warning' : 'success' },
      );
      onAssigned();
      onClose();
    } catch {
      notify('Failed to assign SP3', { type: 'error' });
    } finally {
      setAssigning(false);
    }
  };

  const selectedFuneral = funerals.find((f) => f.id === selectedFuneralId);
  const selectedSuggestion = suggestions.find((x) => x.id === selectedFuneralId);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        Assign SP-3 {record?.form_number} to a case
        {record && (
          <Typography variant="body2" color="text.secondary">
            SIED: <strong>{record.last_name}, {record.first_name}</strong> · died {record.date_of_death?.slice(0, 10) || '—'}
          </Typography>
        )}
      </DialogTitle>
      <DialogContent>
        {suggestions.length > 0 && (
          <Box mb={1.5}>
            <Typography variant="overline" color="text.secondary">Suggested</Typography>
            <Box display="flex" flexDirection="column" gap={1}>
              {suggestions.map((sug) => (
                <Box
                  key={sug.id}
                  role="button"
                  tabIndex={0}
                  onClick={() => setSelectedFuneralId(sug.id)}
                  onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && setSelectedFuneralId(sug.id)}
                  sx={{
                    border: 1,
                    borderColor: selectedFuneralId === sug.id ? 'primary.main' : 'divider',
                    bgcolor: selectedFuneralId === sug.id ? 'action.selected' : 'transparent',
                    borderRadius: 1.5,
                    px: 1.5,
                    py: 1,
                    cursor: 'pointer',
                  }}
                >
                  <Typography variant="body2" fontWeight={600}>
                    #{sug.id} · {sug.name}
                    {sug.maidenName && (
                      <Typography component="span" variant="body2" color="text.secondary" fontWeight={500}>
                        {' '}née {sug.maidenName}
                      </Typography>
                    )}
                  </Typography>
                  <Box display="flex" flexWrap="wrap" gap={0.5} mt={0.5}>
                    {sug.reasons.map((r) => (
                      <Box key={r.code} component="span" sx={REASON_SX(r.strong)}>
                        {r.label}
                        {r.strong ? ' ✓' : ''}
                      </Box>
                    ))}
                  </Box>
                </Box>
              ))}
            </Box>
            <Typography variant="overline" color="text.secondary" display="block" mt={1.5}>
              All cases
            </Typography>
          </Box>
        )}
        <TextField
          autoFocus
          fullWidth
          size="small"
          margin="dense"
          placeholder="Search all funerals by name or number"
          helperText="Deceased name, funeral number (839) or SP-3 number (7840460)"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
            endAdornment: loadingFunerals ? (
              <InputAdornment position="end">
                <CircularProgress size={16} />
              </InputAdornment>
            ) : undefined,
          }}
        />
        {/* Only the first load blanks the list; a re-search keeps the current
            rows on screen and shows its spinner in the search box instead. */}
        {funerals.length === 0 && loadingFunerals ? (
          <Box display="flex" justifyContent="center" py={4}>
            <CircularProgress />
          </Box>
        ) : funerals.length === 0 ? (
          <Box py={4} textAlign="center">
            <Typography variant="body2" color="text.secondary">
              {debouncedSearch
                ? `No funeral matches "${debouncedSearch}".`
                : 'No funerals found.'}
            </Typography>
          </Box>
        ) : (
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>ID</TableCell>
                <TableCell>Deceased Name</TableCell>
                <TableCell>Created</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {funerals.map((f) => (
                <TableRow
                  key={f.id}
                  hover
                  selected={selectedFuneralId === f.id}
                  onClick={() => setSelectedFuneralId(f.id)}
                  sx={{ cursor: 'pointer' }}
                >
                  <TableCell>{f.id}</TableCell>
                  <TableCell sx={{ fontWeight: selectedFuneralId === f.id ? 'bold' : 'normal' }}>
                    {f.deceased_last_name || '-'}, {f.deceased_first_name || '-'}
                  </TableCell>
                  <TableCell>{formatDate(f.created_at)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={assigning}>Cancel</Button>
        <Button
          variant="contained"
          onClick={() => void handleAssign()}
          disabled={!selectedFuneralId || assigning}
          startIcon={assigning ? <CircularProgress size={18} color="inherit" /> : <AssignmentIcon />}
        >
          {assigning
            ? 'Assigning...'
            : selectedFuneral || selectedSuggestion
              ? `Assign to #${selectedFuneralId}`
              : selectedFuneralId
                ? `Assign to #${selectedFuneralId}`
                : 'Select a case'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const SiedPage = () => {
  const [records, setRecords] = useState<SiedRecord[]>([]);
  const [status, setStatus] = useState<SiedStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [checking, setChecking] = useState(false);
  const [assignRecord, setAssignRecord] = useState<SiedRecord | null>(null);
  const [filter, setFilter] = useState<RecordFilter>('all');
  const notify = useNotify();

  const fetchData = useCallback(async () => {
    try {
      const [recordsRes, statusRes] = await Promise.all([
        apiFetch('sied/overview'),
        apiFetch('sied/status'),
      ]);
      setRecords(recordsRes.records || []);
      setStatus(statusRes);
    } catch (error) {
      notify('Failed to fetch SIED data', { type: 'error' });
    } finally {
      setLoading(false);
    }
  }, [notify]);

  useEffect(() => {
    void fetchData();
    const interval = setInterval(() => void fetchData(), 30000);
    return () => clearInterval(interval);
  }, [fetchData]);

  // Poll more frequently while a check is running
  useEffect(() => {
    if (!checking && !status?.isRunning) return;
    const interval = setInterval(() => {
      void apiFetch('sied/status').then((statusRes) => {
        setStatus(statusRes);
        if (!statusRes.isRunning) {
          setChecking(false);
          void fetchData();
        }
      });
    }, 2000);
    return () => clearInterval(interval);
  }, [checking, status?.isRunning, fetchData]);

  // Not-linked SP-3s first (they need a person), then newest. Removed ones
  // (T-148) only under the Removed filter.
  const counts = useMemo(() => siedListCounts(records), [records]);
  const shown = useMemo(() => siedListShown(records, filter), [records, filter]);

  const handleCheck = async () => {
    setChecking(true);
    try {
      await apiFetch('sied/check', { method: 'POST' });
      notify('SIED check started', { type: 'info' });
    } catch (error) {
      notify('Failed to start SIED check', { type: 'error' });
      setChecking(false);
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight={400}>
        <CircularProgress />
      </Box>
    );
  }

  const isRunning = checking || status?.isRunning;
  const progress = status?.progress;
  const showStepper = isRunning || progress?.step === 'error' || progress?.step === 'done';

  return (
    <Box p={3}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Box display="flex" alignItems="center" gap={2}>
          <Typography variant="h5">SIED Records</Typography>
          <Box display="flex" gap={0.75}>
            {(
              [
                ['all', 'All'],
                ['unlinked', 'Not linked'],
                ['turn', 'Our turn'],
                ['removed', 'Removed'],
              ] as Array<[RecordFilter, string]>
            )
              // Removed appears only once something has been removed.
              .filter(([key]) => key !== 'removed' || counts.removed > 0 || filter === 'removed')
              .map(([key, label]) => (
                <Chip
                  key={key}
                  label={`${label} ${counts[key]}`}
                  size="small"
                  variant={filter === key ? 'filled' : 'outlined'}
                  color={key === 'unlinked' && counts.unlinked > 0 ? 'warning' : 'default'}
                  onClick={() => setFilter(key)}
                />
              ))}
          </Box>
        </Box>
        <Box display="flex" alignItems="center" gap={2}>
          {status?.lastCheckedAt && (
            <Typography variant="body2" color="text.secondary">
              Last checked: {formatDate(status.lastCheckedAt)} ({timeAgo(status.lastCheckedAt)})
            </Typography>
          )}
          <Button
            variant="contained"
            startIcon={isRunning ? <CircularProgress size={18} color="inherit" /> : <RefreshIcon />}
            onClick={() => void handleCheck()}
            disabled={!!isRunning}
          >
            {isRunning ? 'Checking...' : 'Check Now'}
          </Button>
        </Box>
      </Box>

      {/* Progress stepper */}
      {showStepper && progress && (
        <Paper sx={{ p: 2, mb: 3 }}>
          <Stepper activeStep={getActiveStep(progress)} alternativeLabel>
            {STEPS.map((s) => (
              <Step
                key={s.key}
                completed={progress.completedSteps.includes(s.key)}
              >
                <StepLabel
                  error={isStepError(progress, s.key)}
                  StepIconProps={
                    isStepError(progress, s.key)
                      ? { icon: <ErrorIcon color="error" /> }
                      : undefined
                  }
                >
                  {s.label}
                </StepLabel>
              </Step>
            ))}
          </Stepper>
          {progress.detail && (
            <Typography variant="body2" color="text.secondary" textAlign="center" mt={1}>
              {progress.detail}
            </Typography>
          )}
        </Paper>
      )}

      {/* Error alert */}
      {status?.lastError && !isRunning && (
        <Alert severity="error" sx={{ mb: 3 }}>
          <Typography variant="body2" fontWeight="bold">Last check failed</Typography>
          <Typography variant="body2" sx={{ mt: 0.5, fontFamily: 'monospace', fontSize: '0.8rem' }}>
            {status.lastError}
          </Typography>
        </Alert>
      )}

      {/* Done alert */}
      {progress?.step === 'done' && !isRunning && !status?.lastError && (
        <Alert severity="success" sx={{ mb: 3 }}>
          {progress.detail}
        </Alert>
      )}

      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell></TableCell>
              <TableCell>SP-3</TableCell>
              <TableCell>Deceased (as in SIED)</TableCell>
              <TableCell>Date of Death</TableCell>
              <TableCell align="center">SIED</TableCell>
              <TableCell>Linked case</TableCell>
              <TableCell>Detected</TableCell>
              <TableCell align="right">PDF</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {shown.length === 0 ? (
              <TableRow>
                <TableCell colSpan={8} align="center">
                  <Typography variant="body2" color="text.secondary" py={4}>
                    {records.length === 0
                      ? 'No records yet. Click "Check Now" to fetch from SIED.'
                      : filter === 'removed'
                        ? 'No removed SP-3s.'
                      : 'Nothing matches this filter.'}
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              shown.map((record) => (
                <TableRow
                  key={record.id}
                  sx={isNew(record.detected_at) ? { backgroundColor: '#e8f5e9' } : {}}
                >
                  <TableCell sx={{ width: 40 }}>
                    {isNew(record.detected_at) && (
                      <FiberNewIcon color="success" fontSize="small" titleAccess="New (last 24h)" />
                    )}
                  </TableCell>
                  <TableCell sx={{ fontFamily: 'monospace', whiteSpace: 'nowrap' }}>{record.form_number}</TableCell>
                  <TableCell>
                    <strong>{record.last_name}</strong>, {record.first_name}
                  </TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>{record.date_of_death?.slice(0, 10) || '-'}</TableCell>
                  <TableCell align="center">
                    <SiedStatusCell record={{ ...record, region: 'quebec' }} />
                  </TableCell>
                  <TableCell>
                    {record.dismissed_at ? (
                      <RemovedCell record={record} onRestored={() => void fetchData()} />
                    ) : (
                      <LinkedCaseCell record={record} onAssign={setAssignRecord} />
                    )}
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" color="text.secondary">
                      {timeAgo(record.detected_at)}
                    </Typography>
                  </TableCell>
                  <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                    <Sp3Download record={record} />
                    {!record.dismissed_at && (
                      <Sp3RowMenu
                        formNumber={record.form_number}
                        downloadedStatus={record.sp3_downloaded_status}
                        canRemove={!record.linked_case}
                        onDone={() => void fetchData()}
                      />
                    )}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Box mt={2}>
        <Typography variant="body2" color="text.secondary">
          {counts.all} record{counts.all !== 1 ? 's' : ''} tracked · {counts.unlinked} not linked to a case
        </Typography>
      </Box>

      <AssignModal
        open={Boolean(assignRecord)}
        record={assignRecord}
        onClose={() => setAssignRecord(null)}
        onAssigned={() => void fetchData()}
      />
    </Box>
  );
};

export default SiedPage;
